import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "@tanstack/react-router";
import {
  PanelLeftClose,
  PanelLeftOpen,
  LayoutDashboard,
  Inbox,
  PlusCircle,
  BookOpen,
  BarChart3,
  FileBarChart,
  Settings,
  SlidersHorizontal,
  Activity,
  Search,
  UserCircle,
  ShoppingBag,
  CheckSquare,
  Command,
  Users2,
  Home,
  Ticket,
  Laptop,
  ListTodo,
  MoreHorizontal,
  Eye,
  EyeOff,
  Workflow,
  PieChart,
} from "lucide-react";
import arsLogo from "@/assets/ars-logo.png";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Avatar } from "./Avatar";
import { ThemeToggle } from "./ThemeToggle";
import { NotificationsBell } from "./NotificationsBell";
import { AssistantLauncher } from "./support-agent/AssistantLauncher";
import { CommandPalette } from "./CommandPalette";
import {
  Feature,
  Permission,
  useIsAdministrator,
  useIsAgent,
  useSession,
  type FeatureName,
  type PermissionName,
} from "./session-provider";
import { todoSummary } from "@/lib/todos";
import { useIsMobile } from "@/hooks/use-mobile";
import { useRefreshOnFocus } from "@/lib/use-refresh-on-focus";
import { desktopPermission, useDesktopNotifications } from "@/lib/desktop-notifications";
import { FollowUpReminderCard } from "@/components/todos/FollowUpReminderCard";
import { DesktopNudgeBar } from "@/components/DesktopNudgeBar";
import { NewBadge } from "@/components/NewBadge";
import { isNew, markFeatureSeen } from "@/lib/whats-new";

/**
 * `requires` gates the item on a permission from the server.
 *
 * ⚠️ Hiding a link is a UI courtesy, not a control. The route and its API still refuse
 * the request server-side; this only avoids showing people buttons that would fail.
 *
 * `backlog` marks scope deferred to a later phase. ⚠️ Shown to ADMINISTRATORS ONLY, and only
 * when one of them ASKS — see `useShowBacklog`. Hidden from everybody else, always.
 *
 * They were disabled-with-a-BL-badge for every user, so that "the shape of the product
 * stays legible". That is a real benefit and it accrues entirely to the people building the
 * product. To an employee at one of ~29 sites, six greyed rows reading "Service catalog",
 * "Approvals", "Knowledge base" are six things the service desk appears to offer and does
 * not — and "BL-002" explains nothing to somebody who has never seen the backlog. The
 * predictable outcome is a ticket asking why the knowledge base does not open.
 *
 * ⚠️ Administrators no longer keep them BY DEFAULT. The legibility argument holds for the
 * handful of people who discuss what is coming, and it held for the whole of every day they
 * were signed in — five rows that do not respond to a click, permanently above Analytics and
 * Settings, on the navigation an administrator uses to do their actual job. They are now
 * behind a toggle that defaults to hidden, so the product an administrator sees is the
 * product that exists, and what is planned is one click away when that is the question.
 */
type NavItem = {
  to: string;
  label: string;
  icon: typeof Home;
  requires?: PermissionName;

  /**
   * Any ONE of these is enough.
   *
   * ⚠️ For a screen several permissions open — the reports surface is the first, where
   * `report.self`, `report.team` and `report.read` all reach it at different widths (D-67).
   * Gating such an item on `requires` would mean picking one, and picking the widest hides the
   * page from exactly the people it was built for while picking the narrowest hides it from the
   * administrators who already hold reporting.
   *
   * ⚠️ It mirrors the server's own any-of policy rather than inventing a second opinion. Which
   * of them somebody holds still decides what the screen SHOWS — that is resolved per request,
   * server-side, and never here.
   */
  requiresAny?: readonly PermissionName[];

  backlog?: string;

  /**
   * A live count drawn beside the label.
   *
   * ⚠️ A NUMBER and never a name. The follow-up count is fetched on every screen in the
   * product — so its payload carries no title, no request and no date, which is what makes it
   * safe to have everywhere.
   */
  badge?: "follow-ups";

  /**
   * The id in `NEW_FEATURES` this row announces, while it is still recent.
   *
   * ⚠️ **A decoration, and it must never be the only announcement of anything that changes
   * what a person may see.** It runs on the reader's own workstation clock against a stamp in
   * their own browser, so it is absent on a second device and gone after a fortnight — which
   * is fine for "there is a new screen" and is no way at all to tell somebody their access
   * changed. Those still go through a notification, which is composed per recipient (D-17).
   *
   * ⚠️ It rides on a row this list has ALREADY filtered by permission, so it can never
   * advertise a screen the reader cannot open — and it is never set on a `backlog` row, which
   * goes nowhere at all.
   */
  newFeature?: string;

  /**
   * A feature flag the entry is switched off with.
   *
   * ⚠️ Distinct from `requires`, which asks whether this PERSON may reach the screen. This asks
   * whether the ORGANISATION has the screen at all — so an administrator turning it off removes
   * it for everybody rather than only for people who happen to lack a permission.
   */
  feature?: FeatureName;

  /**
   * A server-issued boolean on the session that has to be true.
   *
   * ⚠️ **A FOURTH KIND OF GATE, because the knowledge base's audience is not a permission and
   * not a feature flag.** `requires` asks whether this person holds something; `feature` asks
   * whether the organisation has the screen at all. This asks whether an administrator has
   * rolled the screen out to this particular person yet — a pilot of named colleagues, then
   * everybody — which neither of the other two can express.
   *
   * ⚠️ It reads a value the SERVER computed from the same definition its routes apply, and
   * never a rule this bundle evaluates. That is the whole point: a rollout the browser
   * interpreted would be a second opinion about who has been told a product exists, and the
   * copy that drifts is the one nobody watches.
   */
  sessionFlag?: "canSeeKnowledgeBase" | "canAuthorKnowledge";
};

/**
 * One navigation list for everyone, filtered by permission.
 *
 * The mock kept two lists and picked between them with a client-side `mode` flag, which
 * is why any user could reach the agent console by editing localStorage (defect D1). A
 * single list gated on server-issued permissions has no such switch to flip.
 */
const primaryNav: NavItem[] = [
  { to: "/", label: "Home", icon: Home },

  // No "Get help" entry. The gradient "New ticket" button in the header goes to the same
  // place, sits above this list, and is visible at every breakpoint — two controls one
  // above the other doing the identical thing reads as two different features.
  //
  // The mobile bottom bar keeps its own "New", because there the header button collapses
  // to a bare icon and the bottom bar is the primary reach on a phone.
  { to: "/tickets", label: "Tickets", icon: Ticket, requires: Permission.TicketRead },

  // ⚠️ `ticket.read`, which every employee at ~29 sites holds — this is the first screen built
  // for all of them at once. It is NOT the analytics screen with a wider gate: every figure on
  // it is pinned server-side to requests the reader raised or was invited onto (D-169), so an
  // agent opening it sees their own requests and not their team's queue.
  {
    to: "/my-activity",
    label: "My activity",
    icon: PieChart,
    requires: Permission.TicketRead,
    feature: Feature.MyActivity,
    newFeature: "my-activity",
  },

  // ⚠️ No "/team" entry. BL-004 was DISCARDED, not deferred — its two halves shipped
  // elsewhere and the stub outlived them: CSAT and the reputation cards are on the analytics
  // screen (D-63, D-64, D-66), and team membership is administered under Admin. What the
  // placeholder still promised was the mock's technician SCORECARD, which D-55 ruled out on
  // purpose — no composite score, no rank formula, no medals — so leaving it advertised a
  // page nobody intends to build.
  { to: "/catalog", label: "Service catalog", icon: ShoppingBag, backlog: "BL-001" },
  { to: "/approvals", label: "Approvals", icon: CheckSquare, backlog: "BL-001" },
  // ⚠️ ticket.update, which is what "works requests" means here — agents, team leads and
  // administrators hold it; the baseline Requester role every employee at ~29 sites holds
  // does not. A personal follow-up list offered to 2,600 people who raise one request a year
  // is a PHI store with no purpose. The endpoints are gated on the same permission.
  {
    to: "/todos",
    label: "Follow-ups",
    icon: ListTodo,
    requires: Permission.TicketUpdate,
    badge: "follow-ups",
  },
  // ⚠️ No longer a `backlog` row. It is a real screen now, gated on the ROLLOUT rather than on
  // a permission: reading a published article is baseline, so there is no permission to name.
  // Off by default for everybody except the desk, until an administrator switches it on.
  {
    to: "/kb",
    label: "Knowledge base",
    icon: BookOpen,
    feature: Feature.KnowledgeBase,
    sessionFlag: "canSeeKnowledgeBase",
  },
  // ⚠️ report.read, NOT report.export. Gating the screen on export meant that letting a
  // team lead see how their own queue was performing required handing them the largest
  // disclosure the product permits, so nobody was given either. Reading a chart takes
  // nothing out of the system; the figures behind it are scoped by the same query filter
  // and RLS as the queue, so this widens what somebody may see ABOUT what they can already
  // read and never what they can read.
  {
    to: "/analytics",
    label: "Analytics",
    icon: BarChart3,
    requires: Permission.ReportRead,
  },

  // The reports surface (D-67). ⚠️ ANY of the three permissions that open it, mirroring the
  // server's own any-of policy rather than picking one: gating on the widest would hide the
  // page from the agents it was built for, and gating on the narrowest would hide it from the
  // administrators who already hold reporting.
  //
  // ⚠️ Reaching it is not the same as what it shows. An agent sees their own work, a lead sees
  // their team's queues, an administrator sees the organisation — decided by the server per
  // request, pinned into the query, and never by this list.
  {
    to: "/reports",
    label: "Reports",
    icon: FileBarChart,
    requiresAny: [Permission.ReportSelf, Permission.ReportTeam, Permission.ReportRead],
  },

  // Moved out of Admin, and the move is the point. Admin configures the SHAPE of the
  // system — roles, teams, categories — which somebody visits rarely and deliberately.
  // Looking up a colleague is neither: an agent does it mid-ticket, several times a day,
  // and two clicks inside a settings area made it feel like administration rather than a
  // tool. The privilege boundary did not move with it — still user.manage to reach, and
  // still decided per row by the server once inside.
  { to: "/people", label: "People", icon: Users2, requires: Permission.UserManage },
  { to: "/status", label: "System status", icon: Activity, backlog: "BL-008" },

  // Automation rules (D-146). automation.manage is Owner and Super Admin, and it makes its
  // holder an administrator (D-36). Rendering only: every route under /api/automation
  // re-checks, and a rule may only carry actions its author holds the permission for.
  {
    to: "/automation",
    label: "Automation",
    icon: Workflow,
    requires: Permission.AutomationManage,
  },

  // No "Profile" row. It is reachable from the card at the foot of this sidebar and from
  // the avatar in the header, both of which show you WHOSE profile it is — a third entry
  // in this list said the same thing a second time and pushed Admin and Settings down.
  // ⚠️ Every permission that opens at least one tab, not just team.manage. The page has
  // five tabs behind five different grants, and gating the door on one of them meant an
  // auditor with audit.read, or somebody holding only role.manage or category.manage, was
  // shown no link to the one screen they could actually use. The route was reachable by
  // typing the URL the whole time and each tab checks its own permission, so this was never
  // a control — it was a missing door, the same defect the mobile nav had.
  {
    to: "/admin",
    label: "Admin",
    icon: Settings,
    requiresAny: [
      Permission.RoleManage,
      Permission.TeamManage,
      Permission.CategoryManage,
      Permission.SettingsManage,
      Permission.AuditRead,
    ],
  },

  // Gated on settings.read, not team.manage. A team administrator manages their team;
  // they have no business seeing which features are on org-wide, and the two permissions
  // are held by different people.
  {
    to: "/settings",
    label: "Settings",
    icon: SlidersHorizontal,
    requires: Permission.SettingsRead,
  },
];

/**
 * The bottom bar on a phone: the four things somebody reaches for constantly.
 *
 * ⚠️ This is deliberately NOT the whole navigation, and everything it leaves out is reached
 * through the "More" sheet beside it — see `overflowNav` below. It used to be the whole of
 * mobile navigation, which meant that on a phone the product was the four entries here and
 * nothing else: an agent could not open their follow-ups, and an administrator could not open
 * Analytics, People, Admin or Settings, because no link to any of them was drawn at that
 * breakpoint. The permissions were right; there was simply no door.
 *
 * ⚠️ Every one of those screens was reachable by typing the URL the whole time. Nothing here
 * was ever a control — the sidebar it mirrors says the same thing, and the server re-checks
 * every request regardless.
 */
const mobileNavItems: NavItem[] = [
  { to: "/", label: "Home", icon: LayoutDashboard },
  { to: "/tickets", label: "Tickets", icon: Inbox, requires: Permission.TicketRead },
  { to: "/new", label: "New", icon: PlusCircle, requires: Permission.TicketCreate },
  { to: "/profile", label: "Profile", icon: UserCircle },
];

/**
 * How many follow-ups are due right now, for the badge in the sidebar.
 *
 * ⚠️ **This is the whole of the reminder mechanism, and that is a deliberate design.** There
 * is no scheduled row, no background worker and no queued message: due-ness is a comparison
 * against the clock, so nothing can drift, nothing is missed because a service was down, and
 * nothing is delivered twice. It also keeps every follow-up out of the ticket bell, where a
 * private note would have sat beside notifications about other people's requests.
 *
 * ⚠️ The cost, stated because it is real: somebody who is not in the application is not
 * reminded. A reminder that reached them anywhere else would be an email carrying text they
 * typed while reading a PHI-bearing request, to a mailbox — which is a disclosure decision
 * this feature does not need to make in order to be useful.
 *
 * ⚠️ Polled on a slow interval and on focus. Anything faster would be a request per agent
 * per few seconds for a number that changes when a clock passes an hour.
 */
function useFollowUpCount(enabled: boolean): { count: number; refresh: () => void } {
  const [count, setCount] = useState(0);

  const read = useCallback(async () => {
    if (!enabled) return;

    try {
      const summary = await todoSummary();
      setCount(summary.dueNow);
    } catch {
      // A badge that cannot load is a badge that is not drawn. It must never surface an
      // error on whatever screen the person was actually using.
      setCount(0);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setCount(0);
      return;
    }

    void read();

    const handle = window.setInterval(() => void read(), 120_000);
    return () => window.clearInterval(handle);
  }, [enabled, read]);

  // Coming back to the tab is the cheapest signal that somebody is about to look at this
  // again, and it is when a two-minute-old count is most likely to be stale.
  useRefreshOnFocus(() => void read());

  // The count feeds the badge AND the due-reminder card, which re-reads when it changes and
  // asks for a fresh count after a snooze — one poll, two readers.
  return { count, refresh: () => void read() };
}

/**
 * Where the "show what is planned" choice is kept.
 *
 * ⚠️ `localStorage`, and it is safe here in the way D1 was not. D1 kept a PRIVILEGE in the
 * browser and let it decide what an agent could do; this decides whether five rows that are
 * disabled text — no link, no route, nothing behind them — are drawn at all. Editing it
 * cannot reveal a screen, because the rows it reveals go nowhere, and the two gates above it
 * still run: an administrator by `AdministrativeGrants` (D-39) is the only person for whom
 * this value is ever read. It is the same argument D-110 makes for the column layout and
 * D-116 for the sticky team scope.
 *
 * ⚠️ Per device, deliberately and stated: "am I looking at the shape of the product or at
 * what it does today" is a question about the session somebody is in, not a preference that
 * should follow them onto a phone.
 */
const SHOW_BACKLOG_KEY = "tf-show-backlog-nav";

/**
 * Whether to draw the deferred entries, and the control that flips it.
 *
 * ⚠️ HIDDEN IS THE DEFAULT, and an unreadable or absent value reads as hidden. Storage can
 * be full, blocked by policy, or hold something written by a different build; every one of
 * those falls to the state that shows the product as it actually is.
 */
function useShowBacklog(): [boolean, () => void] {
  const [show, setShow] = useState(() => {
    try {
      return localStorage.getItem(SHOW_BACKLOG_KEY) === "true";
    } catch {
      return false;
    }
  });

  const toggle = useCallback(() => {
    setShow((current) => {
      const next = !current;

      try {
        // Written rather than removed on the way back to hidden, so the choice survives a
        // reload in both directions — a toggle that only remembers one of its two positions
        // reads as a control that half works.
        localStorage.setItem(SHOW_BACKLOG_KEY, String(next));
      } catch {
        // A preference that cannot be stored is still honoured for this session. Nothing
        // here is worth failing a page load over.
      }

      return next;
    });
  }, []);

  return [show, toggle];
}

/**
 * Where the sidebar's collapsed-or-expanded choice is kept.
 *
 * ⚠️ `localStorage`, on the same argument as `SHOW_BACKLOG_KEY` above: this decides how WIDE
 * a list of links is drawn, never which links are in it. The permission gates run before a row
 * exists to be drawn narrow or wide, so editing the value can reveal nothing (D1 kept a
 * privilege here; this keeps a layout, as D-110 does for the queue's columns and D-116 for
 * the team scope). Per device, deliberately: how much of a screen somebody gives to
 * navigation is about the screen they are sitting at.
 */
const SIDEBAR_COLLAPSED_KEY = "tf-sidebar-collapsed";

/**
 * Whether the sidebar is drawn as icons alone, and the control that flips it.
 *
 * ⚠️ EXPANDED IS THE DEFAULT, and anything but the literal string `"true"` reads as
 * expanded — absent, malformed, blocked by policy. The label is one hover away when
 * somebody does collapse it (see `RailTooltip`), and the choice is remembered either way.
 */
function useSidebarCollapsed(): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });

  const toggle = useCallback(() => {
    setCollapsed((current) => {
      const next = !current;

      try {
        // Written in both directions, so the choice survives a reload either way.
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {
        // Honoured for this session even when it cannot be stored.
      }

      return next;
    });
  }, []);

  return [collapsed, toggle];
}

/**
 * The label a collapsed row shows beside itself on hover or focus.
 *
 * ⚠️ ONE TOOLTIP, RENDERED THROUGH A PORTAL AT A FIXED POSITION, rather than a `title` or an
 * absolutely-positioned span inside each row. The navigation list scrolls when it is taller
 * than the viewport, and an overflow container clips anything positioned outside it — so a
 * span hanging off the right edge of a row would be cut off exactly where the label is. A
 * `title` is never clipped but arrives after a second and cannot be styled or read on focus.
 * A portal is neither clipped nor delayed, and there is only ever one on screen.
 *
 * ⚠️ Text only — a row's label, or a label plus its BL id. Nothing here fetches, and the
 * strings are the same ones the expanded rail draws beside the icons.
 */
type RailTooltip = {
  show: (label: string, anchor: HTMLElement) => void;
  hide: () => void;
};

type RailTooltipState = { label: string; top: number; left: number } | null;

function useRailTooltip(enabled: boolean): [RailTooltipState, RailTooltip] {
  const [tip, setTip] = useState<RailTooltipState>(null);

  const show = useCallback((label: string, anchor: HTMLElement) => {
    const rect = anchor.getBoundingClientRect();
    setTip({ label, top: rect.top + rect.height / 2, left: rect.right + 10 });
  }, []);

  const hide = useCallback(() => setTip(null), []);

  // Expanding the rail while a tooltip is up would leave it floating beside a label that
  // now says the same thing.
  useEffect(() => {
    if (!enabled) setTip(null);
  }, [enabled]);

  return [tip, { show, hide }];
}

function RailTooltipLayer({ tip }: { tip: RailTooltipState }) {
  if (!tip) return null;

  return createPortal(
    <div
      role="tooltip"
      // ⚠️ Positioned through React's style object, which writes CSSOM properties rather than
      // a `style` attribute — and `style-src-attr 'unsafe-inline'` is an accepted residual
      // for Radix regardless (R-5). Nothing here can execute.
      style={{ top: tip.top, left: tip.left }}
      className="pointer-events-none fixed z-50 -translate-y-1/2 whitespace-nowrap rounded-md border border-border bg-popover px-2 py-1 text-xs font-medium text-popover-foreground shadow-md"
    >
      {tip.label}
    </div>,
    document.body,
  );
}

/**
 * The hover and focus handlers that put a collapsed row's label on screen. Nothing is
 * attached when the rail is expanded, because the label is already drawn.
 */
function tooltipHandlers(label: string, tooltip: RailTooltip | undefined) {
  if (!tooltip) return {};

  return {
    onMouseEnter: (e: React.MouseEvent<HTMLElement>) => tooltip.show(label, e.currentTarget),
    onMouseLeave: tooltip.hide,
    onFocus: (e: React.FocusEvent<HTMLElement>) => tooltip.show(label, e.currentTarget),
    onBlur: tooltip.hide,
  };
}

/**
 * The eye beside the navigation, for administrators only.
 *
 * ⚠️ Drawn only when there is something behind it. An administrator on a build with no
 * deferred entries left would otherwise get a control that appears to do nothing.
 */
function BacklogToggle({
  show,
  onToggle,
  count,
  tooltip,
}: {
  show: boolean;
  onToggle: () => void;
  count: number;

  /** Present when the rail is collapsed: the row is an icon and this carries its label. */
  tooltip?: RailTooltip;
}) {
  if (count === 0) return null;

  const Icon = show ? EyeOff : Eye;
  const label = show ? "Hide what is planned" : "Show what is planned";
  const title = show
    ? "Hide what is planned but not built"
    : `Show ${count} planned ${count === 1 ? "entry" : "entries"} that are not built yet`;

  if (tooltip) {
    return (
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={show}
        aria-label={title}
        {...tooltipHandlers(label, tooltip)}
        className="flex w-full items-center justify-center rounded-lg p-2.5 text-sidebar-foreground/45 transition-colors hover:bg-sidebar-accent/40 hover:text-sidebar-foreground/70"
      >
        <Icon className="h-4 w-4 shrink-0" />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={show}
      title={title}
      className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-xs font-medium text-sidebar-foreground/45 transition-colors hover:bg-sidebar-accent/40 hover:text-sidebar-foreground/70"
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="flex-1">{label}</span>
    </button>
  );
}

/**
 * One navigation row, drawn identically in the sidebar and in the mobile "More" sheet.
 *
 * ⚠️ Extracted rather than copied. The sheet needs the same three behaviours the sidebar has
 * — the backlog entries render as disabled text with their BL id, the follow-up count draws
 * beside its label, the current route highlights — and a second copy of that is how a phone
 * comes to show a live link to a screen that is not built yet.
 *
 * Styled on the sidebar tokens, which is why the sheet paints itself `bg-sidebar`.
 */
function NavRow({
  item,
  pathname,
  followUpsDue,
  onNavigate,
  tooltip,
}: {
  item: NavItem;
  pathname: string;
  followUpsDue: number;
  onNavigate?: () => void;

  /**
   * Present when the rail is collapsed. The row is then an icon alone, its label travels
   * on `aria-label` for a screen reader and on the tooltip for everybody else, and the
   * follow-up count becomes a small badge on the icon's corner.
   */
  tooltip?: RailTooltip;
}) {
  const active = item.to === "/" ? pathname === "/" : pathname.startsWith(item.to);
  const Icon = item.icon;
  const collapsed = tooltip !== undefined;

  // ⚠️ Read ONCE per mount rather than on every render. This row re-renders every time the
  // follow-up poll returns, and recomputing would mean reading storage twice a minute to
  // answer a question whose answer changes twice a fortnight. The cost is that a tab left open
  // for days keeps drawing what it drew — which is a small pill, for at most a fortnight.
  const [showNew] = useState(() => (item.newFeature ? isNew(item.newFeature) : false));

  // ⚠️ Stamped on ARRIVAL, not on the click. Somebody reaches a new screen from the command
  // palette, a link on home, or a pasted URL just as readily as from this row, and every one of
  // those is them having been there; a stamp written only by this row's own click would leave
  // the badge up for the full fortnight for anybody who navigates any other way.
  //
  // ⚠️ `markFeatureSeen` keeps the FIRST stamp, which is what makes this safe to run from a
  // render effect that exists twice on a phone — once in the sidebar and once in the "More"
  // sheet. It also means returning to the screen daily cannot push the three days out again.
  const announced = item.newFeature;
  useEffect(() => {
    if (announced !== undefined && active) markFeatureSeen(announced);
  }, [announced, active]);

  if (item.backlog) {
    const planned = `Planned — not in the current release (${item.backlog})`;

    if (collapsed) {
      return (
        <span
          aria-disabled="true"
          aria-label={`${item.label} — ${planned}`}
          tabIndex={0}
          {...tooltipHandlers(`${item.label} · ${item.backlog}`, tooltip)}
          className="flex cursor-not-allowed items-center justify-center rounded-lg p-2.5 text-sidebar-foreground/35"
        >
          <Icon className="h-5 w-5" />
        </span>
      );
    }

    return (
      <span
        aria-disabled="true"
        title={planned}
        className="flex cursor-not-allowed items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-sidebar-foreground/35"
      >
        <Icon className="h-4 w-4" />
        <span className="flex-1">{item.label}</span>
        <span className="rounded bg-sidebar-accent/40 px-1.5 py-0.5 text-[9px] font-semibold tracking-wider text-sidebar-foreground/50">
          {item.backlog}
        </span>
      </span>
    );
  }

  const count = item.badge === "follow-ups" ? followUpsDue : 0;

  // What this row carries besides its name, in one place so the visible tooltip and the
  // accessible name cannot come to say different things about the same row.
  const notes = [count > 0 ? `${count} due` : null, showNew ? "new" : null].filter(
    (note): note is string => note !== null,
  );

  if (collapsed) {
    return (
      <Link
        to={item.to}
        onClick={onNavigate}
        aria-label={notes.length > 0 ? `${item.label}, ${notes.join(", ")}` : item.label}
        {...tooltipHandlers([item.label, ...notes].join(" · "), tooltip)}
        className={cn(
          "relative flex items-center justify-center rounded-lg p-2.5 transition-colors",
          active
            ? "bg-sidebar-accent text-sidebar-accent-foreground shadow-sm"
            : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground",
        )}
      >
        <Icon className="h-5 w-5" />

        {count > 0 && (
          <span
            aria-hidden="true"
            className="absolute -right-0.5 -top-0.5 min-w-4 rounded-full bg-primary px-1 py-px text-center text-[9px] font-semibold tabular-nums leading-tight text-primary-foreground ring-2 ring-sidebar"
          >
            {count > 99 ? "99+" : count}
          </span>
        )}

        {/* ⚠️ Top LEFT, opposite the count, because a row can carry both — the word is already
            on the tooltip and the accessible name, so a dot landing on top of a number would
            lose the only one of the two that is a live figure. */}
        {showNew && (
          <NewBadge variant="dot" className="absolute -left-0.5 -top-0.5 ring-2 ring-sidebar" />
        )}
      </Link>
    );
  }

  return (
    <Link
      to={item.to}
      onClick={onNavigate}
      className={cn(
        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
        active
          ? "bg-sidebar-accent text-sidebar-accent-foreground shadow-sm"
          : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground",
      )}
    >
      <Icon className="h-4 w-4" />
      <span className="flex-1">{item.label}</span>

      {/* Announced only while it is genuinely recent — fourteen days for everybody, three
          after this reader first went there, whichever ends first. See `whats-new.ts`. */}
      {showNew && <NewBadge />}

      {/* ⚠️ Absent at zero rather than showing a 0. A badge that is always there
          stops being read, and this one has one job: to be noticed on the morning
          somebody promised a caller they would ring back. */}
      {count > 0 && (
        <span
          aria-label={`${count} due`}
          className="min-w-5 rounded-full bg-primary px-1.5 py-0.5 text-center text-[10px] font-semibold tabular-nums leading-none text-primary-foreground"
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </Link>
  );
}

export function AppShell({ children }: { children?: React.ReactNode }) {
  const { pathname } = useLocation();
  const { user, can, hasFeature } = useSession();
  const isAgent = useIsAgent();
  const isAdministrator = useIsAdministrator();

  const followUps = useFollowUpCount(
    can(Permission.TicketUpdate) && hasFeature(Feature.TodoFollowUps),
  );
  const followUpsDue = followUps.count;

  // Desktop alerts, for the people who asked for them.
  //
  // ⚠️ IN THE SHELL rather than on a page, because a desktop alert is only useful when nobody
  // is looking at a page — the whole point is the tab somebody left. Mounting it on a route
  // would mean the feature worked only while that route happened to be open.
  //
  // ⚠️ ALL THREE CONDITIONS, and the third is the browser's, which is granted per machine and
  // cannot be stored. With any of them absent the hook makes no request at all, so the poll it
  // runs is paid for only by people who have asked to be interrupted.
  useDesktopNotifications(
    hasFeature(Feature.BrowserNotifications) &&
      (user?.desktopNotifications ?? false) &&
      desktopPermission() === "granted",
  );

  // Closed on every navigation, including a back gesture — a sheet still covering the screen
  // after the route underneath it changed reads as a frozen app.
  const [moreOpen, setMoreOpen] = useState(false);
  useEffect(() => setMoreOpen(false), [pathname]);

  // ⚠️ And closed when the viewport crosses into the sidebar's breakpoint. Turning a large
  // phone to landscape crosses 768px, at which point the sidebar appears and this sheet is
  // redundant — hiding it with a `md:hidden` class instead would have left its overlay on
  // screen, greying out an app that could no longer be dismissed.
  const isMobile = useIsMobile();
  useEffect(() => {
    if (!isMobile) setMoreOpen(false);
  }, [isMobile]);

  const [showBacklog, toggleBacklog] = useShowBacklog();
  const [collapsed, toggleCollapsed] = useSidebarCollapsed();
  const [railTip, railTooltip] = useRailTooltip(collapsed);

  // Present only while the rail is collapsed, which is what tells every row to draw itself
  // as an icon. One value decides both, so a row cannot be narrow without its label
  // reachable, or wide with a tooltip repeating what is already beside it.
  const rowTooltip = collapsed ? railTooltip : undefined;

  // Three independent gates, and all of them have to pass. A permission the caller lacks
  // hides an item from everybody, administrators included — an administrator is not shown a
  // link to something they cannot use. A deferred item is then hidden from everybody who is
  // not one, so the BL badges stay out of the way of the ~29 sites who would read them as
  // features. And it is hidden from administrators too until one asks to see it.
  //
  // ⚠️ `isAdministrator` still gates the toggle itself, so the stored preference is never
  // read for anybody else — the order is "may this person ever see these", then "do they
  // want to right now", never the reverse.
  const visible = (item: NavItem) =>
    (!item.requires || can(item.requires)) &&
    (!item.requiresAny || item.requiresAny.some(can)) &&
    // ⚠️ The flag is checked HERE rather than inside the row, so an entry for a switched-off
    // screen is absent from the count the backlog toggle speaks for and from the mobile
    // sheet as well — one filter, every surface. Rendering only: the route still exists and
    // its endpoint still answers 404 when the flag is off, whatever this bundle believes.
    (!item.feature || hasFeature(item.feature)) &&
    // ⚠️ The rollout gate. Read off the session rather than derived here — see `sessionFlag`.
    // Rendering only: the route exists either way and its endpoints answer 404.
    (!item.sessionFlag || Boolean(user?.[item.sessionFlag])) &&
    (!item.backlog || (isAdministrator && showBacklog));

  const nav = primaryNav.filter(visible);
  const mobileNav = mobileNavItems.filter(visible);

  // How many deferred entries the toggle is speaking for. Counted against the SAME
  // permission gates the list uses, so the control never offers to reveal a row that would
  // then be filtered out anyway.
  const backlogCount = isAdministrator
    ? primaryNav.filter(
        (item) =>
          item.backlog &&
          (!item.requires || can(item.requires)) &&
          (!item.requiresAny || item.requiresAny.some(can)),
      ).length
    : 0;

  /**
   * What the bottom bar cannot fit, for the "More" sheet.
   *
   * ⚠️ Derived from `nav` — the SAME permission-filtered list the sidebar draws — so a phone
   * and a laptop offer one person exactly the same set of screens, and adding an entry to
   * `primaryNav` reaches both. Nothing is enumerated here a second time, because a hand-kept
   * mobile copy is what left Analytics off a phone in the first place.
   *
   * ⚠️ Empty for the baseline `Requester` role every employee holds — their whole navigation
   * is Home and Tickets, both already in the bar — so they get no "More" tab rather than one
   * that opens onto their own name and nothing else.
   */
  const bottomBarPaths = new Set(mobileNav.map((n) => n.to));

  /**
   * ⚠️ Built entries first, deferred ones after, each keeping the sidebar's order within its
   * group — the ONE place a phone deliberately departs from the sidebar's arrangement.
   *
   * The sidebar is a column somebody takes in at a glance, so a `BL-` row costs nothing where
   * it sits. This is a scrolling sheet, and in `primaryNav` order an administrator opens it
   * onto Team, Service catalog and Approvals — three rows that do not respond to a tap —
   * before reaching Analytics. Sinking them keeps the reading somebody came for at the top
   * without hiding what is planned.
   */
  const overflowNav = nav
    .filter((n) => !bottomBarPaths.has(n.to))
    .sort((a, b) => Number(Boolean(a.backlog)) - Number(Boolean(b.backlog)));
  const overflowActive = overflowNav.some((n) => pathname.startsWith(n.to));
  const overflowHasDue = followUpsDue > 0 && overflowNav.some((n) => n.badge === "follow-ups");

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <CommandPalette />

      {/* ⚠️ STICKY, THE HEIGHT OF THE VIEWPORT, AND SCROLLING INSIDE ITSELF. It was an ordinary
          flex column stretched to the page's height, so on any screen taller than one viewport
          the links scrolled away with the content and what remained beside a long queue was an
          empty band of sidebar colour — "the nav bar disappears". The list is the one part
          that may overflow, and it scrolls on its own. */}
      <aside
        className={cn(
          "hidden shrink-0 flex-col border-r border-sidebar-border bg-sidebar transition-[width] duration-200 md:sticky md:top-0 md:flex md:h-dvh",
          collapsed ? "w-16" : "w-64",
        )}
      >
        <div
          className={cn(
            "flex h-16 shrink-0 items-center border-b border-sidebar-border",
            collapsed ? "justify-center px-2" : "gap-2 px-4",
          )}
        >
          {/* The wordmark is 2.7:1 and unreadable at four rem, so a collapsed rail carries the
              toggle alone and the header draws the logo instead (below). */}
          {!collapsed && (
            <Link to="/" className="flex min-w-0 items-center gap-2">
              <img
                src={arsLogo}
                alt="Advanced Recovery Systems"
                className="h-8 w-auto dark:brightness-100 brightness-0"
              />
              <span className="truncate text-[10px] uppercase tracking-wider text-muted-foreground">
                Service Desk
              </span>
            </Link>
          )}

          <button
            type="button"
            onClick={toggleCollapsed}
            aria-expanded={!collapsed}
            aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
            {...tooltipHandlers("Expand navigation", rowTooltip)}
            className={cn(
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sidebar-foreground/60 transition-colors hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground",
              !collapsed && "ml-auto",
            )}
          >
            {collapsed ? (
              <PanelLeftOpen className="h-4 w-4" aria-hidden />
            ) : (
              <PanelLeftClose className="h-4 w-4" aria-hidden />
            )}
          </button>
        </div>

        <nav className={cn("flex-1 space-y-1 overflow-y-auto", collapsed ? "p-2" : "p-3")}>
          {nav.map((n) => (
            <NavRow
              key={n.to}
              item={n}
              pathname={pathname}
              followUpsDue={followUpsDue}
              tooltip={rowTooltip}
            />
          ))}

          {/* ⚠️ BELOW the list rather than above it. The rows it reveals are appended to the
              built ones, so a control that sat at the top would be pointing away from what it
              does — and this is a thing an administrator presses occasionally, never the first
              thing they reach for. */}
          <BacklogToggle
            show={showBacklog}
            onToggle={toggleBacklog}
            count={backlogCount}
            tooltip={rowTooltip}
          />
        </nav>
        <div className={cn("shrink-0 border-t border-sidebar-border", collapsed ? "p-2" : "p-3")}>
          <Link
            to="/profile"
            aria-label={
              collapsed ? `Your profile — ${user?.displayName ?? "Signing in…"}` : undefined
            }
            {...tooltipHandlers(user?.displayName ?? "Your profile", rowTooltip)}
            className={cn(
              "flex items-center rounded-lg hover:bg-sidebar-accent/50",
              collapsed ? "justify-center p-1.5" : "gap-3 p-2",
            )}
          >
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-accent text-sm font-semibold text-accent-foreground">
              {initialsOf(user?.displayName)}
            </div>
            {!collapsed && (
              <div className="flex min-w-0 flex-col leading-tight">
                <span className="truncate text-sm font-medium">
                  {user?.displayName ?? "Signing in…"}
                </span>
                <span className="text-xs text-muted-foreground">
                  {isAgent ? "Service desk" : "Employee"}
                </span>
              </div>
            )}
          </Link>

          {/*
            The running build, for everyone rather than just administrators.

            The person who notices something broken is whoever is looking at it, and the
            first question back is always "what version are you on?". Putting it where they
            already are turns that into a copyable string instead of a screen-sharing
            session.

            Quiet on purpose: small, muted, and below the profile link, so it is findable
            when wanted and invisible the rest of the time. Selectable because its whole
            purpose is to be pasted into a ticket.

            Absent until the session loads rather than rendering a placeholder that could
            be mistaken for a real version. Absent while the rail is collapsed too — it does
            not fit — and one press away, which is the same distance as everything else here.
          */}
          {user?.version && !collapsed && (
            <p
              className="mt-2 select-text px-2 font-mono text-[10px] leading-none tracking-tight text-muted-foreground/60"
              title="Application version"
            >
              v{user.version}
            </p>
          )}
        </div>
      </aside>

      <RailTooltipLayer tip={railTip} />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-border bg-background/80 px-4 backdrop-blur-xl md:px-6">
          {/* The wordmark, on a desk, while the rail is collapsed and cannot carry it. */}
          {collapsed && (
            <Link to="/" className="hidden shrink-0 md:block" aria-label="Home">
              <img
                src={arsLogo}
                alt="Advanced Recovery Systems"
                className="h-7 w-auto dark:brightness-100 brightness-0"
              />
            </Link>
          )}
          <div className="relative hidden flex-1 max-w-lg sm:block">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <button
              onClick={() => {
                window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }));
              }}
              className="flex h-9 w-full items-center gap-2 rounded-lg border border-input bg-secondary/40 pl-9 pr-3 text-left text-sm text-muted-foreground transition hover:text-foreground"
            >
              {/* ⚠️ Names what the palette ACTUALLY does, which is jump to a page and resolve
                  a request number (D-33). It advertised "people" and "articles", and typing
                  either returned nothing: a placeholder is a promise about what typing will
                  do, and one naming a search that does not exist reads as a broken feature
                  rather than an absent one — which is worse, because nobody reports it.

                  People search was offered and declined — it would turn every screen into a
                  staff lookup, which is what D-51 fenced hard for the one that exists.
                  Articles are the knowledge base, BL-002, not built; the nav entry says so
                  and this no longer contradicts it.

                  ⚠️ It matches CommandPalette's own placeholder deliberately. Two strings
                  describing one control is how they come to disagree. */}
              <span className="flex-1">
                {isAgent ? "Jump to… or a request number" : "Jump to…"}
              </span>
              <kbd className="hidden items-center gap-0.5 rounded border border-border bg-background/60 px-1.5 py-0.5 text-[10px] sm:inline-flex">
                <Command className="h-2.5 w-2.5" />K
              </kbd>
            </button>
          </div>
          <div className="flex flex-1 items-center gap-2 sm:hidden">
            <Link to="/" aria-label="Home">
              <img
                src={arsLogo}
                alt="Advanced Recovery Systems"
                className="h-7 w-auto dark:brightness-100 brightness-0"
              />
            </Link>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {/* The other way in — describe the problem to TARS instead of picking a team and
                a category first. Renders nothing of its own when the flag is off or this
                person cannot raise a request at all (`AssistantLauncher`'s own gate). */}
            <AssistantLauncher />
            <Link
              to="/new"
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-gradient-primary px-3 text-sm font-medium text-primary-foreground shadow-glow transition hover:opacity-90"
            >
              <PlusCircle className="h-4 w-4" />
              <span className="hidden sm:inline">New ticket</span>
            </Link>
            <ThemeToggle />
            <NotificationsBell />

            {/*
              Where a signed-in person expects their own face to be, and the reach to
              their profile from every screen at every breakpoint.

              ⚠️ `hasPicture` is deliberately left at its default. This is one avatar for
              one known user, so the 404-then-initials path costs a single request — the
              hint exists for lists of twenty-five, not for this.
            */}
            {user && (
              <Link
                to="/profile"
                aria-label={`Your profile — ${user.displayName}`}
                className={cn(
                  "ml-1 rounded-full ring-offset-2 ring-offset-background transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  pathname.startsWith("/profile")
                    ? "ring-2 ring-primary"
                    : "hover:ring-2 hover:ring-border",
                )}
              >
                <Avatar userId={user.userId} name={user.displayName} hasPicture={user.hasAvatar} />
              </Link>
            )}
          </div>
        </header>

        {/* The ask to turn desktop alerts on: a bar under the header, drawn only for somebody
            who has not, and only once they have been here a while (D-155). */}
        <DesktopNudgeBar />

        <main className="flex-1 pb-16 md:pb-0">{children}</main>

        {/* A follow-up that has come due, as a corner card the person can snooze or close —
            never a dialog over a reply half-written. Fed by the same count as the badge. */}
        <FollowUpReminderCard dueCount={followUpsDue} onChanged={followUps.refresh} />

        {/* Mobile bottom nav */}
        <nav className="fixed inset-x-0 bottom-0 z-30 flex border-t border-border bg-background/95 backdrop-blur-xl md:hidden">
          {mobileNav.map((n) => {
            const active = n.to === "/" ? pathname === "/" : pathname.startsWith(n.to);
            const Icon = n.icon;
            return (
              <Link
                key={n.to}
                to={n.to}
                className={cn(
                  "flex flex-1 flex-col items-center justify-center gap-1 py-2 text-[10px]",
                  active ? "text-primary" : "text-muted-foreground",
                )}
              >
                <Icon className="h-5 w-5" />
                {n.label}
              </Link>
            );
          })}

          {/*
            The rest of this person's navigation, on a phone.

            ⚠️ Drawn only when there is something behind it, so the ~2,600 employees whose
            navigation is Home and Tickets keep the bar they have today rather than gaining a
            button that opens onto nothing.
          */}
          {overflowNav.length > 0 && (
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={moreOpen}
              className={cn(
                "flex flex-1 flex-col items-center justify-center gap-1 py-2 text-[10px]",
                overflowActive || moreOpen ? "text-primary" : "text-muted-foreground",
              )}
            >
              <span className="relative">
                <MoreHorizontal className="h-5 w-5" />

                {/* ⚠️ A dot and not the number. The follow-up count is drawn in full on the
                    row inside; out here it only has to say "there is something to open",
                    and a count on a button labelled "More" describes nothing in particular. */}
                {overflowHasDue && (
                  <span
                    aria-hidden="true"
                    className="absolute -right-1 -top-0.5 h-2 w-2 rounded-full bg-primary ring-2 ring-background"
                  />
                )}
              </span>
              More
            </button>
          )}
        </nav>

        <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
          <SheetContent
            side="bottom"
            className="max-h-[85vh] overflow-y-auto rounded-t-xl border-sidebar-border bg-sidebar p-4"
          >
            <SheetHeader className="mb-3 text-left">
              <SheetTitle className="text-sm font-semibold text-sidebar-foreground">
                More
              </SheetTitle>
            </SheetHeader>

            <div className="space-y-1">
              {overflowNav.map((n) => (
                <NavRow
                  key={n.to}
                  item={n}
                  pathname={pathname}
                  followUpsDue={followUpsDue}
                  onNavigate={() => setMoreOpen(false)}
                />
              ))}

              {/* The same control, because the sheet is the whole of an administrator's
                  navigation on a phone — and the sheet is where five unresponsive rows cost
                  the most, since it scrolls. It does NOT close the sheet: pressing it is a
                  request to look at this list differently, not to leave it. */}
              <BacklogToggle show={showBacklog} onToggle={toggleBacklog} count={backlogCount} />
            </div>

            {/* The running build, for the same reason it sits at the foot of the sidebar: the
                person who notices something broken is whoever is looking at it, and on a phone
                the sidebar carrying this is never drawn. */}
            {user?.version && (
              <p
                className="mt-4 select-text border-t border-sidebar-border px-2 pt-3 font-mono text-[10px] leading-none tracking-tight text-muted-foreground/60"
                title="Application version"
              >
                v{user.version}
              </p>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
}

/**
 * Initials for the avatar.
 *
 * Derived from the display name the server returned, not stored. The mock carried a
 * separate `initials` field that disagreed with the name in its own seed data.
 */
function initialsOf(displayName: string | undefined): string {
  if (!displayName) return "…";

  return displayName
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0]!)
    .join("")
    .slice(0, 2)
    .toUpperCase();
}
